import client from './client';

export interface LoginResponse {
  accessToken: string;
  franchiseId: string;
}

export interface BranchStatus {
  storeId: string;
  storeName: string;
  todayTotal: number;
  currentWaiting: number;
  entered: number;
  noShow: number;
  canceled: number;
}

export interface DashboardData {
  franchiseName: string;
  totalWaiting: number;
  totalEntered: number;
  totalNoShow: number;
  totalCanceled: number;
  branches: BranchStatus[];
}

export interface BranchInfo {
  storeId: string;
  storeName: string;
  phone: string;
  address: string;
  licenseStatus: string;
  licenseKey: string;
}

export interface ComparisonRow {
  storeId: string;
  storeName: string;
  totalWaiting: number;
  noShowRate: number;
  avgWaitMinutes: number;
}

export async function login(name: string, password: string): Promise<LoginResponse> {
  const res = await client.post('/api/franchise/login', { name, password });
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '로그인에 실패했습니다.');
  }
  const payload = data.data || data;
  return {
    accessToken: payload.accessToken,
    franchiseId: payload.franchiseId,
  };
}

export async function getDashboard(franchiseId: string | number): Promise<DashboardData> {
  const res = await client.get(`/api/franchise/${franchiseId}/dashboard`);
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '대시보드 조회 실패');
  }
  const payload = data.data || data;

  // 백엔드 응답을 프론트 인터페이스에 맞게 매핑
  return {
    franchiseName: payload.franchiseName,
    totalWaiting: payload.totals?.todayTotal ?? 0,
    totalEntered: payload.totals?.todaySeated ?? 0,
    totalNoShow: payload.totals?.todayNoShow ?? 0,
    totalCanceled: payload.totals?.todayCancelled ?? 0,
    branches: (payload.branches || []).map((b: any) => ({
      storeId: b.storeId,
      storeName: b.storeName || b.branchName,
      todayTotal: b.todayTotal ?? 0,
      currentWaiting: b.currentWaiting ?? 0,
      entered: b.todaySeated ?? 0,
      noShow: b.todayNoShow ?? 0,
      canceled: b.todayCancelled ?? 0,
    })),
  };
}

export async function getBranches(franchiseId: string | number): Promise<BranchInfo[]> {
  const res = await client.get(`/api/franchise/${franchiseId}/branches`);
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '지점 목록 조회 실패');
  }
  const payload = data.data || data;

  // 백엔드는 raw FranchiseBranch 엔티티를 반환 (store, license relations 포함)
  return (Array.isArray(payload) ? payload : []).map((b: any) => ({
    storeId: b.storeId || b.store?.id,
    storeName: b.store?.name || b.branchName || '-',
    phone: b.store?.phone || '',
    address: b.store?.address || '',
    licenseStatus: b.license?.status || '-',
    licenseKey: b.license?.licenseKey || '-',
  }));
}

export async function getProfile(
  franchiseId: string | number,
): Promise<{ id: string; name: string; isActive: boolean; createdAt: string }> {
  const res = await client.get(`/api/franchise/${franchiseId}/profile`);
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '프로필 조회 실패');
  }
  const payload = data.data || data;
  return {
    id: payload.id,
    name: payload.name,
    isActive: payload.isActive,
    createdAt: payload.createdAt,
  };
}

export async function changePassword(
  franchiseId: string | number,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await client.patch(`/api/franchise/${franchiseId}/password`, {
    currentPassword,
    newPassword,
  });
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '비밀번호 변경 실패');
  }
}

export async function getComparison(
  franchiseId: string | number,
  from: string,
  to: string,
): Promise<ComparisonRow[]> {
  const res = await client.get(`/api/franchise/${franchiseId}/comparison`, {
    params: { from, to },
  });
  const data = res.data;
  if (data.success === false) {
    throw new Error(data.error || '비교 데이터 조회 실패');
  }
  const payload = data.data || data;

  return (Array.isArray(payload) ? payload : []).map((r: any) => ({
    storeId: r.storeId,
    storeName: r.branchName || r.storeName || '-',
    totalWaiting: r.totalWaitings ?? r.totalWaiting ?? 0,
    noShowRate: r.noShowRate ?? 0,
    avgWaitMinutes: r.avgWaitMinutes ?? 0,
  }));
}
